import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ONBOARDING_STEPS,
  EXTENDED_STEPS,
  type OnboardingStepKey,
} from "@/types/onboarding";

interface UseOnboardingStepOpts {
  hasUser: boolean;
  hasBusiness: boolean;
  /** When false, the hook will not clamp or rewrite the URL query param. */
  ready: boolean;
  /**
   * Dev-only: expose every onboarding step and allow any `?s=` value so UI
   * can be exercised without signing up or creating a business.
   */
  devUnlockNavigation?: boolean;
  /**
   * Grocery stores (GRA-87) have no integrations to connect: their numbers
   * come from report uploads, so the integrations step is left out.
   */
  vertical?: "sales" | "dvh";
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function useOnboardingStep(opts: UseOnboardingStepOpts) {
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleSteps = useMemo<OnboardingStepKey[]>(
    () =>
      ONBOARDING_STEPS.filter(
        (key) => !(opts.vertical === "dvh" && key === "integrations"),
      ),
    [opts.vertical],
  );

  const minIndex = useMemo(() => {
    if (opts.devUnlockNavigation) return 0;
    if (!opts.hasUser) return visibleSteps.indexOf("set-name");
    if (!opts.hasBusiness) return visibleSteps.indexOf("create-business");
    return visibleSteps.indexOf("business-profile");
  }, [opts.devUnlockNavigation, opts.hasUser, opts.hasBusiness, visibleSteps]);

  const maxIndex = useMemo(() => {
    if (opts.devUnlockNavigation) return visibleSteps.length - 1;
    if (!opts.hasUser) return 0;
    if (!opts.hasBusiness) return 1;
    return visibleSteps.length - 1;
  }, [opts.devUnlockNavigation, opts.hasUser, opts.hasBusiness, visibleSteps]);

  const rawKey = searchParams.get("s") as OnboardingStepKey | null;
  const rawIndex = rawKey ? visibleSteps.indexOf(rawKey) : -1;
  const currentIndex = clamp(
    rawIndex >= 0 ? rawIndex : minIndex,
    minIndex,
    maxIndex,
  );
  const stepKey = visibleSteps[currentIndex]!;

  useEffect(() => {
    if (!opts.ready) return;
    if (rawKey !== stepKey) {
      setSearchParams({ s: stepKey }, { replace: true });
    }
  }, [opts.ready, rawKey, stepKey, setSearchParams]);

  const setStepKey = useCallback(
    (key: OnboardingStepKey) => {
      setSearchParams({ s: key }, { replace: false });
    },
    [setSearchParams],
  );

  const nextStep = useCallback(() => {
    const next = currentIndex + 1;
    if (next < visibleSteps.length) {
      setStepKey(visibleSteps[next]!);
    }
  }, [currentIndex, visibleSteps, setStepKey]);

  const prevStep = useCallback(() => {
    const prev = currentIndex - 1;
    if (prev >= minIndex) {
      setStepKey(visibleSteps[prev]!);
    }
  }, [currentIndex, minIndex, visibleSteps, setStepKey]);

  const canSkip = EXTENDED_STEPS.includes(stepKey);
  const isFirst = currentIndex <= minIndex;
  const isLast = currentIndex >= visibleSteps.length - 1;

  const totalSteps = visibleSteps.length;
  const currentStepNumber = currentIndex + 1;

  return {
    stepKey,
    setStepKey,
    nextStep,
    prevStep,
    canSkip,
    isFirst,
    isLast,
    totalSteps,
    currentStepNumber,
    visibleSteps,
  };
}
